import React from 'react';
import numeral from 'numeral';
import { isArray, isEmpty, keyBy, mapValues, chunk, } from 'lodash';
import qs from 'qs';
import retry from 'async-retry';
import { TextDecoder, } from 'text-encoding';

import env from './env';
import { prefectures } from './shared/config';
import { serial } from './shared/util';

const { entries, } = Object;
const prefecturesByPref = mapValues(keyBy(entries(prefectures), '1'), '0');

export function yen(value) {
  return (
    <>
      <span className="mr-1">&yen;</span>
      <span>{numeral(value).format('0,0')}</span>
    </>
  );
}

export function newId(lastItem) {
  return ((lastItem ? parseInt(lastItem.id.replace(/\D/g, ''), 10) : 0) + 1).toString();
};

export function readFile(file, type = 'readAsText') {
  const reader = new FileReader();
  reader[type](file);
  return new Promise((resolve) => {
    reader.addEventListener('load', _ => resolve(_.target.result));
  });
};

export async function readCsvFile(file, fallbackEncoding = 'Shift_JIS') {
  const buffer = await readFile(file, 'readAsArrayBuffer');
  const bytes = new Uint8Array(buffer);
  if(bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf) {
    return new TextDecoder('UTF-8').decode(buffer).replace(/^\uFEFF/, '');
  }
  try {
    return new TextDecoder('UTF-8', { fatal: true }).decode(buffer);
  } catch(e) {
    return new TextDecoder(fallbackEncoding).decode(buffer);
  }
};

export async function getAddressByPostalCode (postalCode) {
  const data = await (await fetch(`https://apis.postcode-jp.com/api/v6/postcodes/${postalCode}?apikey=${env('POSTCODE_API_KEY')}`)).json();
  if(!isArray(data)) return [];

  return data.map(({ prefCode, city, town, }) => ({
    prefecture: Number(prefCode).toString(),
    city,
    address1: town,
  }));
};

export function fullPathWithParams(params, { pathname, search }) {
  const currentParams = qs.parse(decodeURI(search.slice(1)));
  const newParams = {
    ...currentParams,
    ...params
  };
  const newSearch = qs.stringify(newParams);
  return `${pathname}${newSearch ? `?${newSearch}` : ''}`;
};

export function withConfirm(message, f) {
  return (...args) => {
    if(!window.confirm(message)) return;

    f(...args);
  };
};

export function isMobile() {
  return window.matchMedia && window.matchMedia('(max-device-width: 640px)').matches;
}

export function safePrefix(value) {
  return value.includes('-') ? env('TENANT_PREFIX') + value.replace(env('TENANT_PREFIX'), '') : value;
}

// SKUの重複を防ぎつつproducts一括登録を行う。
// rows: [{ productId, sku, data }] productId は呼び出し側で採番済み、data は doc に書き込む内容
// productsRef: tenants/{tid}/products の CollectionReference
// db: firestore() インスタンス
// 既存SKUを直接クエリして重複あれば throw → なければ batch で書き込む。
// クライアントSDKのトランザクションは Query を get できないため、重複チェックは
// runTransaction の外でクエリし、書き込みは batch で行う。
// 大量(数千件)インポートでも Firestore を過負荷にしないよう、チャンクは serial で順次処理する。
// 途中のチャンクで重複検知して throw した場合、それ以前にコミット済みのチャンクは登録されたまま残る。
// in クエリ制限(10件)に合わせて SKU を chunk して読む。書き込みも400件ずつ別 batch に分ける。
export async function createProductsWithSkuCheck(db, productsRef, rows) {
  if(rows.length === 0) return;
  // 登録対象全体でのSKU重複チェック(チャンクをまたいだ重複も検出する)
  const allSkus = rows.map(_ => _.sku).filter(_ => _ != null && _ !== '');
  if(allSkus.length !== new Set(allSkus).size) {
    const duplicatesInBatch = allSkus.filter((s, i, a) => a.indexOf(s) !== i);
    throw new Error('登録対象内にSKUが重複しています: ' + [...new Set(duplicatesInBatch)].join(', '));
  }
  await serial(chunk(rows, 400), async (partRows) => {
    const skus = partRows.map(_ => _.sku).filter(_ => _ != null && _ !== '');
    // 既存SKUとの重複チェック(in クエリも serial で順次実行し同時接続数を抑える)
    const snapshots = await serial(chunk(skus, 10), _ => productsRef.where('sku', 'in', _).get());
    const existingSkus = snapshots.flatMap(s => s.docs.map(d => d.data().sku));
    if(existingSkus.length > 0) {
      throw new Error('SKUがすでに登録されています: ' + [...new Set(existingSkus)].join(', '));
    }
    const batch = db.batch();
    partRows.forEach(({ productId, data }) => {
      batch.set(productsRef.doc(productId), data);
    });
    await batch.commit();
  });
}

export function productSkuErrors(skus, i, productsGroupedBySku) {
  const sku = skus[i];
  return [
    !sku && 'SKUは必須です',
    sku && isEmpty(productsGroupedBySku[sku]) && 'SKUに該当する商品がありません',
    (productsGroupedBySku[sku] || []).length > 1 && 'SKUに該当する商品が複数あります',
    sku && skus.slice(0, i).includes(sku) && 'SKUがCSV内で重複しています',
  ].filter(_ => _);
}
